/**
 * FO-434 (batch-59) — werkbonnen samenvoegen en als bundel naar Finqle.
 *
 * Gedeeld contract tussen de API-route (`/werkbonnen/bundel-verzend-finqle*`) en
 * het backoffice-bevestigingsscherm: één Zod-schema voor de request-body (de
 * client valideert ermee vóór verzenden, de API parset ermee) plus de DTO's van
 * het bevestigingsscherm. Zo kan het formulier per definitie niets sturen dat de
 * API weigert.
 *
 * Begrippen:
 *  - ZENDING = één groep werkbonnen (dezelfde BV x dezelfde klant) die als één
 *    aanlevering naar het Finqle-parse-adres van die BV gaat. Krijgt één
 *    zending-id (cuid), ook als hij door de mailgrootte in meerdere DELEN valt.
 *  - DEEL = één mail binnen een zending ("deel 1/2"). Nooit een stille afkap.
 *  - UITVALLER = een geselecteerde werkbon die niet mee kan, mét reden.
 */
import { z } from 'zod'
import type { WerkbonGoedkeuringStatus } from './werkbonnen'

/**
 * Bijlage-vorm zoals een gebruiker hem ooit koos: één samengevoegde PDF of losse
 * PDF's per bon. batch-65 (FO-484): op de FINQLE-route bestaat alleen nog
 * SAMENGEVOEGD (één verzamelbon per deel); LOSSE_BIJLAGEN blijft als type bestaan
 * voor een oude opgeslagen voorkeur (die de Finqle-route negeert, met uitleg) en
 * voor de klant-mail (samenvoegen -> contactpersoon).
 */
export const BUNDEL_BIJLAGE_MODI = ['SAMENGEVOEGD', 'LOSSE_BIJLAGEN'] as const
export type BundelBijlageModus = (typeof BUNDEL_BIJLAGE_MODI)[number]

/**
 * batch-65 (FO-484 §A.2) - de ENIGE bijlage-vorm op de Finqle-route: één
 * verzamelbon per deel. Losse bijlagen leveren bij Finqle per definitie n
 * verzoeken, n goedkeuringen en n facturen op.
 */
export const FINQLE_BUNDEL_MODUS = 'SAMENGEVOEGD' as const satisfies BundelBijlageModus
export type FinqleBundelModus = typeof FINQLE_BUNDEL_MODUS

/** De uitleg die de gebruiker ziet als hij (nog) losse bijlagen had gekozen (T6). */
export const FINQLE_LOSSE_BIJLAGEN_UITLEG =
  "Losse bijlagen kunnen niet meer naar Finqle: Finqle maakt van elke losse PDF een eigen verzoek, met een eigen goedkeuring en een eigen factuur. De werkbonnen gaan daarom als één verzamelbon (één document, één totaal). Losse PDF's per werkbon kun je nog wel naar de contactpersoon van de klant sturen via 'Samenvoegen'."

/**
 * T6 - de bijlage-vorm op de Finqle-route: ALTIJD `SAMENGEVOEGD`. Een gevraagde
 * of onthouden `LOSSE_BIJLAGEN` wordt genegeerd en levert de uitleg op, zodat de
 * gebruiker ziet waarom zijn oude keuze niet meer geldt. Puur (drift-test).
 */
export function bepaalFinqleBundelModus(args: {
  gevraagd?: BundelBijlageModus | null
  opgeslagen?: BundelBijlageModus | null
}): { modus: FinqleBundelModus; melding: string | null } {
  const losGevraagd = args.gevraagd === 'LOSSE_BIJLAGEN'
  const losOnthouden = args.gevraagd == null && args.opgeslagen === 'LOSSE_BIJLAGEN'
  return {
    modus: FINQLE_BUNDEL_MODUS,
    melding: losGevraagd || losOnthouden ? FINQLE_LOSSE_BIJLAGEN_UITLEG : null,
  }
}

/**
 * Redenen waarom een geselecteerde werkbon niet meegaat. Spiegelt 1-op-1 de
 * statussen van de gedeelde verzend-gate (`checkVerzendbaar`), aangevuld met de
 * twee gevallen die pas bij het renderen blijken.
 */
export const BUNDEL_UITVAL_REDENEN = [
  'NOT_FOUND',
  'INVALID_STATE',
  'BESLOTEN',
  'GEEN_ENTITEIT',
  'GEEN_ADRES',
  // batch-63 werkbon-facturatie (FO-475) — de al-gefactureerd-gate.
  'AL_GEFACTUREERD',
  'NIET_FACTURABEL',
  'ALREADY',
  'REDEN_VEREIST',
  'DUPLICAAT_INHOUD',
  'PDF_FAILED',
  'PDF_TE_GROOT',
  'ERROR',
  // batch-67 verzamelbon-uren (FO-507, integrator-review): de werkbon heeft
  // regels die de verzamelbon met alleen uren niet kan dragen (materiaal,
  // doorbelaste inkoop, vast bedrag, ...): die zouden bij Finqle stil wegvallen.
  'NIET_UREN_REGELS',
  // batch-69 werkbon-pauze (FO-511 §C6): de factuurpauze is op een of meer dagen
  // geen geldende klantregel en de verzend-gate staat aan.
  'KLANTPAUZE_ONBEVESTIGD',
] as const
export type BundelUitvalReden = (typeof BUNDEL_UITVAL_REDENEN)[number]

/** Korte labels voor het bevestigingsscherm en de uitslag. */
export const BUNDEL_UITVAL_LABELS: Record<BundelUitvalReden, string> = {
  NOT_FOUND: 'Werkbon niet gevonden',
  INVALID_STATE: 'Geen interne goedkeuring',
  BESLOTEN: 'Besloten werkbon',
  GEEN_ENTITEIT: 'Geen BV gekoppeld',
  GEEN_ADRES: 'BV zonder Finqle-adres',
  AL_GEFACTUREERD: 'Al gefactureerd',
  NIET_FACTURABEL: 'Niet te factureren',
  ALREADY: 'Al naar Finqle verzonden',
  REDEN_VEREIST: 'Opnieuw verzenden vereist een reden',
  DUPLICAAT_INHOUD: 'Zelfde inhoud als een al verzonden werkbon',
  PDF_FAILED: 'PDF genereren mislukt',
  PDF_TE_GROOT: 'PDF te groot voor de mail',
  ERROR: 'Onverwachte fout',
  NIET_UREN_REGELS: 'Bevat regels die geen uren zijn',
  KLANTPAUZE_ONBEVESTIGD: 'Pauzeregel van de klant niet bevestigd',
}

// ─── Request-schemas ────────────────────────────────────────────────────────

/**
 * Max. 200 bonnen per aanroep, gelijk aan de bestaande bulk-route: de bundel is
 * een alternatieve aanlevervorm van dezelfde selectie, geen andere schaal.
 */
const werkbonIdsSchema = z.array(z.string().min(1)).min(1).max(200)

/**
 * Preview en verzenden nemen exact dezelfde body: de verzending doet bewust
 * dezelfde gate-ronde nog een keer, dus één schema (geen tweede bron van
 * waarheid die uit elkaar kan lopen).
 */
const bundelBodySchema = z.object({
  werkbonIds: werkbonIdsSchema,
  /** Herverzenden: al verzonden bonnen tellen dan gewoon mee. */
  opnieuw: z.boolean().optional(),
  /**
   * FO-444 (spec 144) — verplichte reden bij het opnieuw verzenden van een al
   * verzonden bon (audit); zonder reden valt zo'n bon uit met REDEN_VEREIST.
   */
  reden: z.string().trim().max(500).nullable().optional(),
  /**
   * Oude clients sturen hier nog een bijlage-vorm mee. batch-65 (FO-484): de
   * Finqle-route accepteert het veld (geen onterechte validatiefout), maar
   * verstuurt altijd één verzamelbon per deel; `LOSSE_BIJLAGEN` wordt genegeerd
   * met uitleg (`bepaalFinqleBundelModus`).
   */
  modus: z.enum(BUNDEL_BIJLAGE_MODI).optional(),
  /**
   * batch-65 (FO-484 §A.1) — de werkbonnen als bijlagepagina's achter de
   * verzamelbon (zonder eigen totaal of akkoord). Default aan: de klant en de
   * beoordelaar bij Finqle controleren zo de uren per bon. Uit = alleen de
   * verzamelbon zelf (één kop, één regeltabel, één totaal, één akkoordblok).
   */
  metBijlagen: z.boolean().optional(),
})

/** FO-444 — venster waarbinnen een tweede Finqle-zending als "zojuist" geldt. */
export const FINQLE_HERVERZEND_VENSTER_MINUTEN = 10

/**
 * FO-444 (spec 144 par. 3.B.10) — één Finqle-zending van een werkbon in de
 * verzendhistorie: uit de audit (wie/wanneer/opnieuw/reden/bundel) gekoppeld aan
 * de mail in de uitbox (status/bezorging).
 */
export interface FinqleZendingDTO {
  id: string
  verzondenOp: string
  doorNaam: string | null
  opnieuw: boolean
  reden: string | null
  bundel: boolean
  zendingId: string | null
  /**
   * batch-63 (FO-475) — bij een bundel: hoeveel werkbonnen er in deze zending
   * meegingen (over alle delen), voor "Samengevoegd naar Finqle op dd-MM-yyyy
   * (bundel n bonnen)". null bij een losse verzending.
   */
  bundelGrootte: number | null
  deelNr: number | null
  aantalDelen: number | null
  /** batch-65 (FO-484) - de verzamelbon van deze zending (null bij losse en oudere zendingen). */
  verzamelbonId: string | null
  verzamelbonNummer: string | null
  outgoingEmailId: string | null
  mailStatus: string | null
  mailSentAt: string | null
  mailAttempts: number | null
  mailTo: string | null
  mailFout: string | null
  note: string | null
}

export const BundelPreviewRequestSchema = bundelBodySchema
export type BundelPreviewRequest = z.infer<typeof BundelPreviewRequestSchema>

export const BundelVerzendRequestSchema = bundelBodySchema
export type BundelVerzendRequest = z.infer<typeof BundelVerzendRequestSchema>


// ─── DTO's ──────────────────────────────────────────────────────────────────

export interface BundelWerkbonDTO {
  id: string
  werkbonNumber: string
}

export interface BundelUitvallerDTO {
  id: string
  werkbonNumber: string | null
  reden: BundelUitvalReden
  /** Mensleesbare uitleg van de gate (of de rendermelding). */
  toelichting: string
  /** FO-413/FO-420 — backoffice-pad dat de weigering direct oplost. */
  herstelPad?: string
}

/**
 * Eén zending: alle bonnen van dezelfde BV x klant, in werkbonnummer-volgorde.
 * Bewust GEEN `aantalDelen`: de mailgrootte-splitsing hangt af van de gerenderde
 * PDF-groottes en is pas bij het versturen bekend (renderen voor een preview zou
 * elke bon twee keer door de PDF-generator halen). De uitslag meldt de delen.
 */
export interface BundelGroepDTO {
  /** Stabiele sleutel binnen deze preview (`entityId:clientId`). */
  sleutel: string
  entityId: string
  entityName: string
  clientId: string
  clientName: string
  finqleEmail: string
  werkbonnen: BundelWerkbonDTO[]
}

/**
 * batch-63 (FO-475) — één geselecteerde werkbon op het bevestigingsscherm: wat
 * is het, en gaat hij mee? Alle bonnen van de selectie staan erin, ook de
 * uitvallers, zodat de gebruiker per bon ziet waarom hij (niet) meegaat.
 */
export interface BundelBonDTO {
  id: string
  werkbonNumber: string | null
  klantNaam: string | null
  bvNaam: string | null
  goedkeuringStatus: WerkbonGoedkeuringStatus | null
  /** Het facturatie-label van de werkbonnenlijst (zelfde resolver). */
  facturatieLabel: string | null
  /** Gaat mee in een zending. */
  mee: boolean
  /** Waarom niet (alleen als `mee` false is). */
  reden?: string
  uitvalReden?: BundelUitvalReden
  /** De zending waarin hij meegaat (`BundelGroepDTO.sleutel`). */
  groepSleutel?: string
  /**
   * batch-69 (FO-511) — gaat mee, maar de factuurpauze is op een of meer dagen
   * geen bevestigde klantregel (de verzend-gate staat nog uit). Nooit stil.
   */
  waarschuwing?: string
}

export interface BundelPreviewDTO {
  /** batch-65 (FO-484) - altijd SAMENGEVOEGD: één verzamelbon per deel. */
  modus: FinqleBundelModus
  /** batch-65 (T6) - uitleg als de gebruiker (nog) losse bijlagen had gekozen; anders null. */
  modusMelding: string | null
  groepen: BundelGroepDTO[]
  uitvallers: BundelUitvallerDTO[]
  /** Totaal aantal bonnen dat meegaat (som over de groepen). */
  aantalMee: number
  /** batch-63 (FO-475) — elke geselecteerde bon met zijn uitkomst (mee / uitgesloten met reden). */
  bonnen: BundelBonDTO[]
  /**
   * batch-63 (FO-475) — de selectie beslaat meer dan één klant x BV: dan gaat er
   * per combinatie een eigen zending uit (de `valideerWerkbonMerge`-regel: één
   * zending = één klant + één BV). Het scherm stelt die splitsing voor.
   */
  splitsVoorstel: boolean
}

export interface BundelVerzendDeelDTO {
  deelNr: number
  aantalDelen: number
  to: string
  /** De werkbonnen die in dit deel meegingen (de inhoud van de mail). */
  werkbonNummers: string[]
  outgoingEmailId: string | null
  /** Mail geaccepteerd (QUEUED/SENT) of geblokkeerd/gedropt. */
  verzonden: boolean
  /**
   * batch-65 (FO-484) - de verzamelbon van dit deel (één document, één totaal).
   * Null als het deel niet verzonden is (het nummer is dan niet uitgegeven).
   */
  verzamelbonId: string | null
  verzamelbonNummer: string | null
  /** batch-65 - hoe het deel bij Finqle aankwam: de parse-mail of (§B) de API. */
  kanaal: 'MAIL' | 'FINQLE_API'
  reden?: string
  /**
   * Werkbonnen die WEL meegingen in dit deel maar niet als verzonden gemarkeerd
   * konden worden (rij verdwenen tijdens het verzenden). Ze staan ook bij de
   * uitvallers; hier zodat de uitslag niet doet alsof ze netjes zijn afgerond.
   */
  nietGemarkeerd?: string[]
}

export interface BundelVerzendGroepDTO {
  sleutel: string
  entityName: string
  clientName: string
  finqleEmail: string
  /** Bundel-referentie: alle bonnen van deze zending delen dit zending-id. */
  zendingId: string
  delen: BundelVerzendDeelDTO[]
  /** Werkbonnummers die daadwerkelijk gemarkeerd zijn als verzonden. */
  verzondenNummers: string[]
  /** batch-63 (FO-475) — dezelfde bonnen mét id (doorklik naar de verzendhistorie). */
  verzonden: BundelWerkbonDTO[]
  /** batch-63 (FO-475) — het verzendmoment (ISO) van de eerste gemarkeerde bon; null als er niets ging. */
  verzondenOp: string | null
}

export interface BundelVerzendResultDTO {
  /** batch-65 (FO-484) - altijd SAMENGEVOEGD: één verzamelbon per deel. */
  modus: FinqleBundelModus
  /** batch-65 (T6) - uitleg als er (nog) losse bijlagen gevraagd/onthouden waren. */
  modusMelding: string | null
  groepen: BundelVerzendGroepDTO[]
  uitvallers: BundelUitvallerDTO[]
  /**
   * batch-69 (FO-511) — verstuurd, maar zonder bevestigde klantpauze (de
   * verzend-gate staat nog uit): per werkbon de uitleg. Leeg = niets aan de hand.
   */
  waarschuwingen?: Array<{ id: string; werkbonNumber: string; toelichting: string }>
  samenvatting: {
    zendingen: number
    verzonden: number
    uitgevallen: number
    mislukt: number
  }
}
